import {
  ReactNode,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState
} from 'react';
import { createPortal } from 'react-dom';
import BaseButton from './BaseButton';
import { t } from 'services/i18n.service';
import { cn } from 'utils/cn';

type Placement = 'top' | 'bottom' | 'left' | 'right';
type Variant = 'default' | 'warning' | 'info';

interface ConfirmPopupProps {
  children: ReactNode;
  message?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  placement?: Placement;
  variant?: Variant;
  onConfirm: () => void;
  onCancel?: () => void;
  className?: string;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

const variantStyles: Record<Variant, { container: string; confirm: string }> = {
  default: {
    container:
      'bg-white dark:bg-secondary-dark-200 border-gray-200 dark:border-secondary-dark-500',
    confirm:
      'bg-primary-700 text-white hover:bg-primary-800 dark:bg-secondary-dark-500 dark:text-secondary-dark-900 dark:hover:bg-secondary-dark-600'
  },
  warning: {
    container:
      'bg-white dark:bg-secondary-dark-200 border-red-200 dark:border-red-800',
    confirm:
      'bg-red-500 text-white hover:bg-red-600 dark:bg-red-600 dark:hover:bg-red-700'
  },
  info: {
    container:
      'bg-white dark:bg-secondary-dark-200 border-blue-200 dark:border-blue-800',
    confirm:
      'bg-blue-500 text-white hover:bg-blue-600 dark:bg-blue-600 dark:hover:bg-blue-700'
  }
};

const POPUP_GAP = 8;
const VIEWPORT_PADDING = 8;

const arrowStyles: Record<Placement, string> = {
  top: 'top-full left-1/2 -translate-x-1/2 border-t-gray-200 dark:border-t-secondary-dark-500 border-l-transparent border-r-transparent border-b-transparent border-t-[6px] border-l-[6px] border-r-[6px] border-b-0',
  bottom:
    'bottom-full left-1/2 -translate-x-1/2 border-b-gray-200 dark:border-b-secondary-dark-500 border-l-transparent border-r-transparent border-t-transparent border-b-[6px] border-l-[6px] border-r-[6px] border-t-0',
  left: 'left-full top-1/2 -translate-y-1/2 border-l-gray-200 dark:border-l-secondary-dark-500 border-t-transparent border-b-transparent border-r-transparent border-l-[6px] border-t-[6px] border-b-[6px] border-r-0',
  right:
    'right-full top-1/2 -translate-y-1/2 border-r-gray-200 dark:border-r-secondary-dark-500 border-t-transparent border-b-transparent border-l-transparent border-r-[6px] border-t-[6px] border-b-[6px] border-l-0'
};

const ConfirmPopup = ({
  children,
  message,
  confirmLabel,
  cancelLabel,
  placement = 'top',
  variant = 'default',
  onConfirm,
  onCancel,
  className,
  open: controlledOpen,
  onOpenChange
}: ConfirmPopupProps) => {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const isControlled = controlledOpen !== undefined;
  const isOpen = isControlled ? controlledOpen : uncontrolledOpen;

  const setIsOpen = useCallback(
    (value: boolean) => {
      if (!isControlled) setUncontrolledOpen(value);
      onOpenChange?.(value);
    },
    [isControlled, onOpenChange]
  );

  const [activePlacement, setActivePlacement] = useState(placement);
  const triggerRef = useRef<HTMLDivElement>(null);
  const popupRef = useRef<HTMLDivElement>(null);
  const [popupPosition, setPopupPosition] = useState({ top: 0, left: 0 });

  const clamp = (value: number, min: number, max: number) =>
    Math.min(Math.max(value, min), max);

  const getPopupPosition = useCallback(
    (
      nextPlacement: Placement,
      triggerRect: DOMRect,
      popupRect: DOMRect
    ): { top: number; left: number } => {
      let top = 0;
      let left = 0;

      if (nextPlacement === 'top') {
        top = triggerRect.top - popupRect.height - POPUP_GAP;
        left = triggerRect.left + triggerRect.width / 2 - popupRect.width / 2;
      } else if (nextPlacement === 'bottom') {
        top = triggerRect.bottom + POPUP_GAP;
        left = triggerRect.left + triggerRect.width / 2 - popupRect.width / 2;
      } else if (nextPlacement === 'left') {
        top = triggerRect.top + triggerRect.height / 2 - popupRect.height / 2;
        left = triggerRect.left - popupRect.width - POPUP_GAP;
      } else {
        top = triggerRect.top + triggerRect.height / 2 - popupRect.height / 2;
        left = triggerRect.right + POPUP_GAP;
      }

      return {
        top: clamp(
          top,
          VIEWPORT_PADDING,
          window.innerHeight - popupRect.height - VIEWPORT_PADDING
        ),
        left: clamp(
          left,
          VIEWPORT_PADDING,
          window.innerWidth - popupRect.width - VIEWPORT_PADDING
        )
      };
    },
    []
  );

  const handleClose = useCallback(() => {
    setIsOpen(false);
    onCancel?.();
  }, [onCancel, setIsOpen]);

  const handleConfirm = useCallback(() => {
    setIsOpen(false);
    onConfirm();
  }, [onConfirm, setIsOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const clickedTrigger = triggerRef.current?.contains(target);
      const clickedPopup = popupRef.current?.contains(target);
      if (!clickedTrigger && !clickedPopup) {
        handleClose();
      }
    };
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') handleClose();
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, handleClose]);

  useLayoutEffect(() => {
    if (!isOpen || !popupRef.current || !triggerRef.current) return;

    const triggerRect = triggerRef.current.getBoundingClientRect();
    const popupRect = popupRef.current.getBoundingClientRect();
    let adjusted = activePlacement;

    if (
      activePlacement === 'top' &&
      triggerRect.top - popupRect.height - POPUP_GAP < VIEWPORT_PADDING
    ) {
      adjusted = 'bottom';
    } else if (
      activePlacement === 'bottom' &&
      triggerRect.bottom + popupRect.height + POPUP_GAP >
        window.innerHeight - VIEWPORT_PADDING
    ) {
      adjusted = 'top';
    } else if (
      activePlacement === 'left' &&
      triggerRect.left - popupRect.width - POPUP_GAP < VIEWPORT_PADDING
    ) {
      adjusted = 'right';
    } else if (
      activePlacement === 'right' &&
      triggerRect.right + popupRect.width + POPUP_GAP >
        window.innerWidth - VIEWPORT_PADDING
    ) {
      adjusted = 'left';
    }

    if (adjusted !== activePlacement) {
      setActivePlacement(adjusted);
      return;
    }

    const nextPosition = getPopupPosition(adjusted, triggerRect, popupRect);
    setPopupPosition((prev) =>
      prev.top === nextPosition.top && prev.left === nextPosition.left
        ? prev
        : nextPosition
    );
  }, [activePlacement, getPopupPosition, isOpen, placement]);

  useEffect(() => {
    setActivePlacement(placement);
  }, [placement]);

  useEffect(() => {
    if (!isOpen) return;
    const handleReposition = () => {
      if (!popupRef.current || !triggerRef.current) return;
      const triggerRect = triggerRef.current.getBoundingClientRect();
      const popupRect = popupRef.current.getBoundingClientRect();
      const nextPosition = getPopupPosition(
        activePlacement,
        triggerRect,
        popupRect
      );
      setPopupPosition((prev) =>
        prev.top === nextPosition.top && prev.left === nextPosition.left
          ? prev
          : nextPosition
      );
    };

    window.addEventListener('resize', handleReposition);
    window.addEventListener('scroll', handleReposition, true);
    return () => {
      window.removeEventListener('resize', handleReposition);
      window.removeEventListener('scroll', handleReposition, true);
    };
  }, [activePlacement, getPopupPosition, isOpen]);

  const styles = variantStyles[variant];

  return (
    // Keyboard users activate the interactive child (e.g. BaseButton), whose
    // activation bubbles here as a click � the wrapper needs no key handler.
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events
    <div
      ref={triggerRef}
      // Uncontrolled mode: the wrapper IS the trigger. Controlled callers
      // (open/onOpenChange) drive visibility themselves, so no handler there.
      // Popup-internal clicks never reach this handler � the portal root
      // below stops their propagation.
      onClick={isControlled ? undefined : () => setIsOpen(true)}
      className={cn('w-full', className)}>
      {children}
      {isOpen &&
        typeof document !== 'undefined' &&
        createPortal(
          // Portals bubble events through the React tree, not the DOM tree,
          // so popup-internal clicks would reach ancestors of this component
          // � our own trigger wrapper (re-opening the popup) and any
          // clickable row/card hosting it. Contain them here.
          // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions
          <div
            ref={popupRef}
            role='dialog'
            aria-modal='true'
            onClick={(event) => event.stopPropagation()}
            style={{
              top: `${popupPosition.top}px`,
              left: `${popupPosition.left}px`
            }}
            className={cn(
              'fixed z-[100010] w-max max-w-[16rem] rounded-md border shadow-lg animate-fadeInSlideDown',
              '~p-2/3',
              styles.container
            )}>
            <div className='w-full flex flex-col ~gap-2/3'>
              <p className='~text-xs/sm text-gray-700 dark:text-secondary-dark-900 text-center'>
                {message ?? t('btn_confirm')}
              </p>
              <div className='flex items-center justify-center ~gap-2/4'>
                <BaseButton
                  title={cancelLabel ?? t('cancel')}
                  variant='secondary'
                  size='xs'
                  onClick={handleClose}
                />
                <BaseButton
                  title={confirmLabel ?? t('btn_confirm')}
                  size='xs'
                  onClick={handleConfirm}
                  additionalStyles={styles.confirm}
                />
              </div>
            </div>
            <div
              className={cn('absolute w-0 h-0', arrowStyles[activePlacement])}
            />
          </div>,
          document.body
        )}
    </div>
  );
};

export default ConfirmPopup;
